import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import AudioUploadProgressModal from '../components/AudioUploadProgressModal';
import AudioProcessingService from '../util/audioProcessingService';
import { useNotifications } from './NotificationsContext';
import { useAlert } from '../components/Alert';

const UploadProgressContext = createContext();

export const UploadProgressProvider = ({ children }) => {
  // Estado básico
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [progressData, setProgressData] = useState({});
  const [queueInfo, setQueueInfo] = useState({ queueSize: 0, queuedFiles: [] });
  const { addTechnicalListener } = useNotifications();
  const { showSuccess, showError } = useAlert();

  // Ref para evitar inicializaciones múltiples
  const initialized = useRef(false);
  const pendingAlertRef = useRef(null);

  // Calcular progreso según estado
  const calculateProgress = useCallback((status) => {
    const statusProgress = {
      'pending': 0,
      'queued': 0,
      'processing': 50,
      'transcribing': 60,
      'analyzing': 85, 
      'completed': 100,
      'error': 100,
      'cancelled': 100
    };
    
    return statusProgress[status.toLowerCase()] || 50;
  }, []);

  // Calcular progreso general
  const calculateOverallProgress = useCallback(() => {
    if (!uploadItems || uploadItems.length === 0) return 0;
    
    let totalProgress = 0;
    let itemsWithProgress = 0;
    
    uploadItems.forEach(item => {
      const progress = progressData[item.label]?.progress || 0;
      if (progress > 0) {
        totalProgress += progress;
        itemsWithProgress++;
      }
    });
    
    return itemsWithProgress > 0 ? Math.round(totalProgress / itemsWithProgress) : 0;
  }, [uploadItems, progressData]);

  // Actualizar progreso de un archivo
  const updateFileProgress = useCallback((filename, progress, status = 'processing', error = '') => {
    setProgressData(prev => ({
      ...prev,
      [filename]: {
        progress,
        status,
        error
      }
    }));
  }, []);

  // Inicializar estado del sistema
  const loadInitialState = useCallback(async () => {
    if (initialized.current) return;
    
    try {
      const statusData = await AudioProcessingService.getSystemStatus();
      console.log("Cargando estado inicial de archivos en procesamiento...", statusData);

      if (statusData.processing && statusData.processing.length > 0) {
        const activeFiles = statusData.processing.map(file => {
          const fileName = file.fileName || file.fileId.split('/').pop() || file.fileId;
          return {
            label: fileName,
            fileId: file.fileId,
            file: { name: fileName }
          };
        });
        
        // Crear datos de progreso
        const newProgress = {};
        activeFiles.forEach(item => {
          const file = statusData.processing.find(f => f.fileId === item.fileId);
          newProgress[item.label] = {
            status: AudioProcessingService.mapStatusToUI(file?.status || 'transcribing'),
            progress: calculateProgress(file?.status || 'transcribing'),
            error: file?.error || ''
          };
        });
        
        setUploadItems(activeFiles);
        setProgressData(newProgress);
      }
      
      // Información de cola
      if (statusData.queueSize > 0) {
        console.log(`Detectados ${statusData.queueSize} archivos en cola`);
        setQueueInfo({
          queueSize: statusData.queueSize,
          queuedFiles: statusData.queued || []
        });
        
      }
      
      // Marcar como inicializado
      initialized.current = true;
    } catch (error) {
      console.error("Error al cargar estado inicial:", error);
    }
  }, [calculateProgress]);

  const handleNotification = useCallback((notification) => {
    console.log("UploadProgress recibió notificación:", notification);
    const queueSize = notification.data && !isNaN(parseInt(notification.data)) 
    ? parseInt(notification.data) 
    : 0;

    // Get fileId from notification
    const fileId = notification.fileId || (notification.data && notification.data.fileId);
    let fileName = notification.fileName || (notification.data && notification.data.href);
    
    // Try to extract name from message if needed
    if (!fileName && notification.message) {
      const messageMatch = notification.message.match(/^([^\s]+\.mp3|[^\s]+\.wav)/) || 
                        notification.message.match(/GUID_[\w-]+/);
      if (messageMatch) {
        fileName = messageMatch[0];
      }
    }
    
    if (!fileName && fileId) {
      fileName = fileId.split('/').pop() || fileId;
    }
    
    // If we don't have any identifier, we can't process
    if (!fileId && !fileName) return;
    
    const displayName = fileName || fileId;
    const trackingId = fileId || fileName;
    
    // Start of audio processing
    if (notification.type === 'init_audio') {
      setUploadItems(prev => {
        const existingIndex = prev.findIndex(item => 
          item.fileId === trackingId || 
          item.label === displayName ||
          (item.file && item.file.name === displayName)
        );
        
        if (existingIndex >= 0) {
          // Replace existing
          const newItems = [...prev];
          newItems[existingIndex] = {
            label: displayName,
            fileId: trackingId,
            file: { name: displayName }
          };
          return newItems;
        } else {
          // Add new
          return [...prev, {
            label: displayName,
            fileId: trackingId,
            file: { name: displayName }
          }];
        }
      });
      if (queueSize > 0) {
        setQueueInfo(prev => ({
          ...prev,
          queueSize: queueSize
        }));
      }
      // Update progress
      setProgressData(prev => ({
        ...prev,
        [trackingId]: {
          status: 'processing',
          progress: calculateProgress('processing'),
          error: ''
        }
      }));
      
    }
    else if (notification.type === 'end_audio') {
      const isSuccess = 'success';
      const status = isSuccess ? 'completed' : 'error';
      
      console.log(`Actualizando archivo ${displayName} a estado: ${status}`);
      pendingAlertRef.current = {
        type: status,
        displayName,
        error: notification.error
      };
      // Find the file in current items
      setUploadItems(prev => {
        const matchingItem = prev.find(item => 
          (trackingId && item.fileId === trackingId) || 
          item.label === displayName ||
          (item.file && item.file.name === displayName) ||
          // Partial match for long names
          (displayName && item.label && item.label.includes(displayName)) ||
          (displayName && item.file && item.file.name && item.file.name.includes(displayName))
        );
        
        if (matchingItem) {
          // Update progress immediately
          setProgressData(prevData => ({
            ...prevData,
            [matchingItem.fileId]: {
              status,
              progress: 100,
              error: status === 'error' ? (notification.error || 'Error en procesamiento') : ''
            }
          }));
          // Schedule removal after a delay
          setTimeout(() => {
            setUploadItems(current => 
              current.filter(item => item.fileId !== matchingItem.fileId)
            );
            
            // If no more files, hide floating indicator
            setTimeout(() => {
              setUploadItems(items => {

                return items;
              });
            }, 200);
          }, 5000);
        } else {
          console.log(`Añadiendo archivo completado: ${displayName}`);
          const newItem = {
            label: displayName,
            fileId: trackingId,
            file: { name: displayName }
          };
          
          setProgressData(prevData => ({
            ...prevData,
            [trackingId]: {
              status,
              progress: 100,
              error: status === 'error' ? (notification.error || 'Error en procesamiento') : ''
            }
          }));
  
          // Schedule removal after delay
          setTimeout(() => {
            setUploadItems(current => current.filter(item => item.fileId !== trackingId));
            
            setTimeout(() => {
              setUploadItems(items => {
                // Check if we need to fetch updated queue status
                if (items.length === 0) {
                  // Simple refresh of system status to update queue info
                  AudioProcessingService.getSystemStatus().then(statusData => {
                    if (statusData.queueSize > 0) {
                      setQueueInfo({
                        queueSize: statusData.queueSize,
                        queuedFiles: statusData.queued || []
                      });
                    }
                  }).catch(err => {
                    console.error("Error checking queue status:", err);
                  });
                }
                return items;
              });
            }, 200);
          }, 5000);
          
          // Add to list
          return [...prev, newItem];
        }
        
        return prev;
      });
    }
  }, [calculateProgress]);

  useEffect(() => {
    if (pendingAlertRef.current) {
      const { type, displayName, error } = pendingAlertRef.current;
      
      if (type === 'completed') {
        showSuccess(
          `El archivo ${displayName} ha sido procesado exitosamente.`,
          'Procesamiento completado'
        );
      } else if (type === 'error') {
        showError(
          `Error al procesar el archivo ${displayName}: ${error || 'Error desconocido'}`,
          'Error de procesamiento'
        );
      }
      
      // Clear the pending alert
      pendingAlertRef.current = null;
    }
  }, [showSuccess, showError, uploadItems]);

  const showProcessingProgress = useCallback((files, progress = {}, queue = 0) => {
    // Verificar que hay algo que mostrar
    if ((!files || files.length === 0) && queue === 0) return;
    console.log("Showing progress for:", { files, queueInfo });

    setUploadItems(files);
    
    // Actualizar progreso
    if (Object.keys(progress).length > 0) {
      setProgressData(progress);
    }
    
    // Actualizar información de cola
    setQueueInfo({
      queueSize: queue,
      queuedFiles: []
    });
    

  }, []);

  // Funciones de UI
  const handleMaximize = useCallback(() => {
    setIsModalVisible(true);
  }, []);
  
  const handleModalDismiss = useCallback(() => {
    setIsModalVisible(false);
  }, [uploadItems.length]);

  // Inicialización - solo una vez
  useEffect(() => {
    if (initialized.current) return;
    
    // Registrar listener para notificaciones técnicas
    const unsubscribe = addTechnicalListener(handleNotification);
    
    // Cargar estado inicial
    loadInitialState();
    
    return () => {
      unsubscribe();
    };
  }, [addTechnicalListener, handleNotification, loadInitialState]);

  return (
    <UploadProgressContext.Provider 
      value={{ 
        calculateOverallProgress,
        showProcessingProgress,
        updateFileProgress,
        uploadItems,
        progressData,
        setIsModalVisible,
        handleMaximize,
        handleModalDismiss,        
        queueInfo,
      }}
    >
      {children}
      
      <AudioUploadProgressModal
        visible={isModalVisible}
        onDismiss={handleModalDismiss}
        uploadItems={uploadItems}
        progressData={progressData}
        queueInfo={queueInfo}
      />
            
    </UploadProgressContext.Provider>
  );
};

export const useUploadProgress = () => {
  const context = useContext(UploadProgressContext);
  if (!context) {
    throw new Error('useUploadProgress debe usarse dentro de UploadProgressProvider');
  }
  return context;
};